import React, { useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';

const cache = {};

// Goldsmith / Customer line on a card; manual tags (tag admins) can switch it to another main-portal entry.
const KINDS = {
  goldsmith: { label: 'Goldsmith', url: '/notes/tag-workspace/goldsmiths', none: 'No goldsmith' },
  customer: { label: 'Customer', url: '/notes/tag-workspace/customers', none: 'No customer' },
};

export function TagPartyLine({ kind, item, onSaved }) {
  const K = KINDS[kind];
  const idKey = `${kind}_id`;
  const tid = `tagws-${kind}`;
  const [editing, setEditing] = useState(false);
  const [smiths, setSmiths] = useState(cache[kind] || []);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = item.is_manual && item.can_modify;
  const start = async () => {
    setVal(item[idKey] || '');
    setEditing(true);
    try { cache[kind] = (await notesApi.get(K.url)).data || []; setSmiths(cache[kind]); } catch (e) { toast.error(extractError(e, `Could not load the ${K.label.toLowerCase()} list.`)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await notesApi.patch(`/notes/tag-workspace/manual/${item.id}`, { [idKey]: val });
      onSaved?.(item.id, val, smiths.find((g) => g.id === val)?.name || '');
      toast.success(`${K.label} updated.`);
      setEditing(false);
    } catch (e) { toast.error(extractError(e, `Could not update the ${K.label.toLowerCase()}.`)); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid={`${tid}-edit`}>
        <select value={val} onChange={(e) => setVal(e.target.value)} className="h-7 min-w-0 flex-1 rounded-md border border-input bg-background px-1.5 text-xs" data-testid={`${tid}-edit-select`}>
          <option value="">{K.none}</option>
          {smiths.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <button type="button" onClick={save} disabled={busy} aria-label={`Save ${K.label.toLowerCase()}`} className="grid h-7 w-7 place-items-center rounded-md text-emerald-600 hover:bg-emerald-50" data-testid={`${tid}-edit-save`}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}</button>
        <button type="button" onClick={() => setEditing(false)} disabled={busy} aria-label="Cancel" className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary" data-testid={`${tid}-edit-cancel`}><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 truncate text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground sm:text-xs" title={item[kind] || K.none} data-testid={`tagws-jewellery-${kind}`}>
      <span className="truncate">{K.label}: <span className="font-medium text-foreground">{item[kind] || '—'}</span></span>
      {canEdit ? <button type="button" onClick={start} aria-label={`Change ${K.label.toLowerCase()}`} title={`Change ${K.label.toLowerCase()}`} className="grid h-5 w-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid={`${tid}-edit-button`}><Pencil size={11} /></button> : null}
    </div>
  );
}
